import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Code, CopyButton, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCopy, IconPrinter } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { countRecoveryCodes, generateRecoveryCodes } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
}

export const RECOVERY_CODE_COUNT_KEY = ['auth', 'recovery-code-count'] as const;

/**
 * Getting a set of recovery codes, and seeing how many are left.
 *
 * WHY A USER SHOULD CARE, said plainly on the screen: the second factor is emailed, so anything
 * that stops mail arriving stops them signing in. That happened on 2026-08-19 — a misconfigured
 * SES quietly discarded every message — and these are the only way back in when it does.
 *
 * SHOWN ONCE. The server keeps hashes, not the codes, so this dialog is the only time they are
 * readable. Reopening it shows a count and an offer to replace them, never the previous set.
 */
export function RecoveryCodesModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const count = useQuery({
    queryKey: RECOVERY_CODE_COUNT_KEY,
    queryFn: ({ signal }) => countRecoveryCodes(signal),
    enabled: opened,
  });

  const generate = useMutation({
    mutationFn: () => generateRecoveryCodes(),
    onSuccess: (issued) => {
      setCodes(issued);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: RECOVERY_CODE_COUNT_KEY });
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('auth.recovery.generateFailed'))
          : t('auth.recovery.generateFailed'),
      ),
  });

  const remaining = count.data ?? 0;
  const hasNone = count.isSuccess && remaining === 0;

  function close() {
    // Cleared on the way out, so reopening cannot show a set again — the server could not tell us
    // what they were even if we asked.
    setCodes(null);
    setError(null);
    onClose();
  }

  return (
    <AppModal opened={opened} onClose={close} title={t('auth.recovery.title')} width="md">
      <Stack gap="md">
        {codes ? (
          <>
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('auth.recovery.showOnce')}
            </Alert>

            {/* Monospaced and in two columns: these get read aloud and copied onto paper, and a
                proportional font makes a 1 and an l the same shape. */}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              {codes.map((code) => (
                <Code key={code} block style={{ fontSize: '1rem', letterSpacing: '0.05em' }}>
                  {code}
                </Code>
              ))}
            </SimpleGrid>

            <Group gap="sm">
              <CopyButton value={codes.join('\n')}>
                {({ copied, copy }) => (
                  <Button
                    variant="default"
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    onClick={copy}
                  >
                    {copied ? t('auth.recovery.copied') : t('auth.recovery.copy')}
                  </Button>
                )}
              </CopyButton>
              <Button
                variant="default"
                leftSection={<IconPrinter size={16} />}
                onClick={() => window.print()}
              >
                {t('auth.recovery.print')}
              </Button>
              <Button color="invetrak" onClick={close}>
                {t('auth.recovery.done')}
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Text size="sm">{t('auth.recovery.why')}</Text>

            {hasNone ? (
              <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                {t('auth.recovery.noneYet')}
              </Alert>
            ) : (
              <Text size="sm" c="dimmed">
                {t('auth.recovery.remaining', { count: remaining })}
              </Text>
            )}

            {error && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                {error}
              </Alert>
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={close}>
                {t('shell.close')}
              </Button>
              <Button color="invetrak" loading={generate.isPending} onClick={() => generate.mutate()}>
                {/* Different words for the two cases: replacing a set that still works is a
                    decision, getting a first set is not. */}
                {remaining > 0 ? t('auth.recovery.replace') : t('auth.recovery.generate')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </AppModal>
  );
}
